import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  Tooltip,
  CircularProgress,
  Skeleton
} from "@mui/material";
import {
  Search,
  CheckCircle,
  Cancel,
  Visibility,
  Close,
  Refresh,
  AccountBalance,
  Image as ImageIcon
} from "@mui/icons-material";
import {
  fetchCoordinatorEventUploadRequests,
  fetchCoordinatorEventUploadFiles,
  reviewCoordinatorEventUploadRequest,
  getFilePresignedViewUrl
} from "../../../services/eventUploadsService";

/**
 * Format ISO or SQL date string to readable format
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

const GeoTagPhotosApproval = ({ eventTypeFilter = "Tech Camp" }) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, reason: "" });

  const [previewImage, setPreviewImage] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Debounce search input by 400ms
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when eventTypeFilter changes
  React.useEffect(() => {
    setPage(0);
  }, [eventTypeFilter]);

  /* ---------------- 1. FETCH EVENT UPLOAD REQUESTS (Server-side search, event_type & pagination) ---------------- */
  const {
    data: requestsResponse,
    isLoading: isRequestsLoading,
    isFetching: isRequestsFetching,
    error: requestsError,
    refetch: refetchRequests
  } = useQuery(
    ["coordinator-event-upload-requests", eventTypeFilter, debouncedSearch, page, rowsPerPage],
    () =>
      fetchCoordinatorEventUploadRequests({
        eventType: eventTypeFilter,
        search: debouncedSearch,
        page: page + 1,
        page_size: rowsPerPage
      }),
    {
      keepPreviousData: true,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    }
  );

  const filteredList = requestsResponse?.data || [];
  const totalCount = requestsResponse?.total ?? 0;

  /* ---------------- 2. FETCH FILES & PRESIGNED IMAGE URLS DIRECTLY ---------------- */
  const {
    data: filesResponse,
    isLoading: isFilesLoading,
    error: filesError
  } = useQuery(
    ["coordinator-event-upload-files", selectedRequest?.request_id],
    async () => {
      const res = await fetchCoordinatorEventUploadFiles(selectedRequest.request_id);
      const files = res?.files || [];

      // Directly fetch presigned image view URLs for all active files
      const filesWithUrls = await Promise.all(
        files.map(async (f) => {
          if (!f.key_name) return { ...f, preview_url: null };
          try {
            const url = await getFilePresignedViewUrl(f.key_name);
            return { ...f, preview_url: url };
          } catch {
            return { ...f, preview_url: null };
          }
        })
      );

      return { ...res, files: filesWithUrls };
    },
    {
      enabled: Boolean(selectedRequest?.request_id && reviewModalOpen),
      staleTime: 60 * 1000
    }
  );

  const activeFiles = filesResponse?.files || [];

  /* ---------------- 3. REVIEW MUTATION (ACCEPT / REJECT) ---------------- */
  const reviewMutation = useMutation(
    ({ requestId, payload }) => reviewCoordinatorEventUploadRequest(requestId, payload),
    {
      onSuccess: (data, variables) => {
        const isApproved = variables.payload.action === "accepted";
        showToast(
          data?.message ||
            (isApproved
              ? "Geo-Tagged Photos approved successfully!"
              : "Geo-Tagged Photos rejected."),
          isApproved ? "success" : "info"
        );
        setRejectDialog({ open: false, reason: "" });
        setReviewModalOpen(false);
        setSelectedRequest(null);
        // Invalidate requests list to reflect updated status
        queryClient.invalidateQueries(["coordinator-event-upload-requests"]);
      },
      onError: (err) => {
        showToast(err?.message || "Failed to process review action.", "error");
      }
    }
  );

  /* ---------------- HANDLERS ---------------- */
  const handleOpenReview = (requestItem) => {
    setSelectedRequest(requestItem);
    setReviewModalOpen(true);
  };

  const handleCloseReview = () => {
    if (reviewMutation.isLoading) return;
    setReviewModalOpen(false);
    setSelectedRequest(null);
  };

  const handleAccept = () => {
    if (!selectedRequest?.request_id) return;
    reviewMutation.mutate({
      requestId: selectedRequest.request_id,
      payload: { action: "accepted" }
    });
  };

  const handleOpenReject = () => {
    setRejectDialog({ open: true, reason: "" });
  };

  const handleConfirmReject = () => {
    if (!selectedRequest?.request_id) return;
    reviewMutation.mutate({
      requestId: selectedRequest.request_id,
      payload: {
        action: "rejected",
        reason: rejectDialog.reason.trim() || undefined
      }
    });
  };

  /**
   * Display exact status string directly from backend
   */
  const getStatusBadge = (status) => {
    if (!status) return null;
    const s = String(status).toLowerCase();
    const isAccepted = s === "accepted" || s === "approved";
    const isRejected = s === "rejected";

    return (
      <Chip
        size="small"
        label={status}
        sx={{
          backgroundColor: isAccepted ? "#f0fdf4" : isRejected ? "#fef2f2" : "#fffbeb",
          color: isAccepted ? "#16a34a" : isRejected ? "#dc2626" : "#d97706",
          fontWeight: 700,
          fontSize: "0.72rem",
          textTransform: "capitalize",
          border: `1px solid ${
            isAccepted ? "#bbf7d0" : isRejected ? "#fecaca" : "#fde68a"
          }`,
          borderRadius: "4px"
        }}
      />
    );
  };

  return (
    <Box sx={{ width: "100%", fontFamily: "'Inter', sans-serif" }}>
      {/* Header & Controls Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2.5
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.1rem" }}
          >
            Geo-Tagged Photos Verification
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
            Review and approve event geo-tagged photos uploaded by host institutions.
          </Typography>
        </Box>

        {/* Action Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
          <TextField
            size="small"
            placeholder="Search institute, event, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                fontSize: "0.8rem",
                height: "36px",
                width: { xs: "100%", sm: "280px" }
              }
            }}
          />
          <Tooltip title="Refresh List">
            <IconButton
              size="small"
              onClick={() => refetchRequests()}
              disabled={isRequestsFetching}
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                p: "6px",
                color: "#64748b",
                "&:hover": { backgroundColor: "#f8fafc", color: "#1877f2" }
              }}
            >
              <Refresh
                sx={{
                  fontSize: 18,
                  animation: isRequestsFetching ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" }
                  }
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error state */}
      {requestsError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: "8px" }}
          action={
            <Button color="inherit" size="small" onClick={() => refetchRequests()}>
              Retry
            </Button>
          }
        >
          {requestsError.message || "Failed to load event upload requests."}
        </Alert>
      )}

      {/* Main Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          overflow: "hidden"
        }}
      >
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ backgroundColor: "#f8fafc" }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: "#64748b",
                  py: 1.5,
                  width: 50
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}
              >
                Institute Name
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}
              >
                Event Type
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}
              >
                Start Date
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}
              >
                End Date
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", py: 1.5 }}
              >
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: "#64748b",
                  py: 1.5,
                  width: 110
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isRequestsLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  <TableCell><Skeleton width={20} /></TableCell>
                  <TableCell><Skeleton width="70%" /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={90} /></TableCell>
                  <TableCell><Skeleton width={90} /></TableCell>
                  <TableCell><Skeleton width={90} /></TableCell>
                  <TableCell align="center"><Skeleton width={70} sx={{ mx: "auto" }} /></TableCell>
                </TableRow>
              ))
            ) : filteredList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", fontSize: "0.85rem" }}
                  >
                    No geo-tagged photo submissions found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredList.map((item, index) => (
                <TableRow
                  key={item.request_id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#f8fafc" },
                    transition: "background-color 0.15s ease"
                  }}
                >
                  <TableCell
                    sx={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 600 }}
                  >
                    {page * rowsPerPage + index + 1}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccountBalance sx={{ fontSize: 16, color: "#94a3b8" }} />
                      <Typography
                        sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}
                      >
                        {item.institute_name || `Institute #${item.institute_id}`}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={item.event_type || item.slot_type || "Event"}
                      sx={{
                        height: "22px",
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        backgroundColor:
                          (item.event_type || "").toUpperCase().includes("FDP")
                            ? "#eff6ff"
                            : "#f0fdf4",
                        color:
                          (item.event_type || "").toUpperCase().includes("FDP")
                            ? "#1877f2"
                            : "#16a34a",
                        border: `1px solid ${
                          (item.event_type || "").toUpperCase().includes("FDP")
                            ? "#bfdbfe"
                            : "#bbf7d0"
                        }`,
                        borderRadius: "4px"
                      }}
                    />
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: "0.82rem", color: "#334155", fontWeight: 500 }}
                  >
                    {formatDate(item.start_date)}
                  </TableCell>

                  <TableCell
                    sx={{ fontSize: "0.82rem", color: "#334155", fontWeight: 500 }}
                  >
                    {formatDate(item.end_date)}
                  </TableCell>

                  <TableCell>{getStatusBadge(item.status)}</TableCell>

                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility sx={{ fontSize: 14 }} />}
                      onClick={() => handleOpenReview(item)}
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "6px",
                        py: 0.4,
                        px: 1.5,
                        color: "#1877f2",
                        borderColor: "#bfdbfe",
                        backgroundColor: "#eff6ff",
                        "&:hover": {
                          backgroundColor: "#dbeafe",
                          borderColor: "#1877f2"
                        }
                      }}
                    >
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      </TableContainer>

      {/* Review Photos Modal */}
      {selectedRequest && (
        <Dialog
          open={reviewModalOpen}
          onClose={handleCloseReview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "14px",
              overflow: "hidden"
            }
          }}
        >
          {/* Modal Header: Displays Institute name, event type chip, status badge, dates and bookslot */}
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              py: 2,
              px: 3,
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0"
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#0f172a" }}
                >
                  {selectedRequest.institute_name || `Institute #${selectedRequest.institute_id}`}
                </Typography>
                {selectedRequest.event_type && (
                  <Chip
                    size="small"
                    label={selectedRequest.event_type || selectedRequest.slot_type}
                    sx={{
                      height: 20,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      backgroundColor:
                        (selectedRequest.event_type || "").toUpperCase().includes("FDP")
                          ? "#eff6ff"
                          : "#f0fdf4",
                      color:
                        (selectedRequest.event_type || "").toUpperCase().includes("FDP")
                          ? "#1877f2"
                          : "#16a34a",
                      border: `1px solid ${
                        (selectedRequest.event_type || "").toUpperCase().includes("FDP")
                          ? "#bfdbfe"
                          : "#bbf7d0"
                      }`,
                      borderRadius: "4px"
                    }}
                  />
                )}
                {getStatusBadge(selectedRequest.status)}
              </Box>
              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem" }}>
                Dates: {formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)}
                {selectedRequest.bookslot_id ? ` • Bookslot #${selectedRequest.bookslot_id}` : ""}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseReview}
              sx={{ color: "#94a3b8" }}
              disabled={reviewMutation.isLoading}
            >
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3, backgroundColor: "#ffffff" }}>
            {/* Photos Gallery Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75
                }}
              >
                <ImageIcon sx={{ fontSize: 16, color: "#1877f2" }} />
                Uploaded Photos ({activeFiles.length})
              </Typography>
              {isFilesLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748b" }}>
                  <CircularProgress size={14} color="inherit" />
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    Loading photos...
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Error loading files */}
            {filesError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                {filesError.message || "Failed to load upload files."}
              </Alert>
            )}

            {/* Photos Gallery Grid */}
            {isFilesLoading ? (
              <Grid container spacing={2}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={`file-skel-${i}`}>
                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: "8px" }} />
                  </Grid>
                ))}
              </Grid>
            ) : activeFiles.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  borderRadius: "8px",
                  border: "1px dashed #e2e8f0",
                  backgroundColor: "#f8fafc"
                }}
              >
                <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                  No photos uploaded for this request.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={1.5}>
                {activeFiles.map((photo) => (
                  <Grid item xs={12} sm={6} md={4} key={photo.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 1,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: "#cbd5e1",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.06)"
                        }
                      }}
                    >
                      {/* Direct Photo Image Render */}
                      <Box
                        onClick={() => {
                          if (photo.preview_url) {
                            setPreviewImage({
                              url: photo.preview_url,
                              title: photo.file_name || "Photo Preview"
                            });
                          }
                        }}
                        sx={{
                          width: "100%",
                          height: 150,
                          borderRadius: "6px",
                          overflow: "hidden",
                          backgroundColor: "#f1f5f9",
                          cursor: photo.preview_url ? "pointer" : "default",
                          border: "1px solid #e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative"
                        }}
                      >
                        {photo.preview_url ? (
                          <img
                            src={photo.preview_url}
                            alt={photo.file_name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover"
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              color: "#94a3b8",
                              gap: 0.5
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 28 }} />
                            <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                              Image unavailable
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* File details & Enlarge action */}
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "#0f172a",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1
                            }}
                            title={photo.file_name}
                          >
                            {photo.file_name}
                          </Typography>
                          {photo.preview_url && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setPreviewImage({
                                  url: photo.preview_url,
                                  title: photo.file_name || "Photo Preview"
                                })
                              }
                              sx={{
                                color: "#1877f2",
                                p: "2px",
                                ml: 0.5,
                                "&:hover": { backgroundColor: "#eff6ff" }
                              }}
                              title="Enlarge Photo"
                            >
                              <Visibility sx={{ fontSize: 15 }} />
                            </IconButton>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontSize: "0.68rem",
                            display: "block"
                          }}
                        >
                          {formatDateTime(photo.uploaded_at)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              py: 2,
              px: 3,
              backgroundColor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              justifyContent: "space-between"
            }}
          >
            <Button
              onClick={handleCloseReview}
              disabled={reviewMutation.isLoading}
              sx={{
                textTransform: "none",
                color: "#64748b",
                fontSize: "0.82rem",
                fontWeight: 600
              }}
            >
              Close
            </Button>

            {selectedRequest.status === "pending" ? (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={reviewMutation.isLoading}
                  startIcon={<Cancel fontSize="small" />}
                  onClick={handleOpenReject}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    borderRadius: "6px",
                    px: 2
                  }}
                >
                  Reject
                </Button>

                <Button
                  variant="contained"
                  disabled={reviewMutation.isLoading}
                  startIcon={
                    reviewMutation.isLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckCircle fontSize="small" />
                    )
                  }
                  onClick={handleAccept}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    borderRadius: "6px",
                    backgroundColor: "#16a34a",
                    "&:hover": { backgroundColor: "#15803d" },
                    px: 2.5,
                    boxShadow: "none"
                  }}
                >
                  {reviewMutation.isLoading ? "Submitting..." : "Accept & Approve"}
                </Button>
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: "#64748b", fontStyle: "italic" }}>
                This request has already been reviewed ({selectedRequest.status}).
              </Typography>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Reject Reason Prompt Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() =>
          !reviewMutation.isLoading && setRejectDialog({ open: false, reason: "" })
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>
          Reject Geo-Tagged Photos
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "#64748b", mb: 1.5, fontSize: "0.8rem" }}
          >
            Please provide a reason or feedback for rejecting these geo-tagged photos:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            disabled={reviewMutation.isLoading}
            placeholder="e.g. Geo-tag location missing, photos not clear, wrong session photos..."
            value={rejectDialog.reason}
            onChange={(e) =>
              setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))
            }
            sx={{ fontSize: "0.82rem" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disabled={reviewMutation.isLoading}
            onClick={() => setRejectDialog({ open: false, reason: "" })}
            sx={{ textTransform: "none", color: "#64748b", fontSize: "0.8rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={reviewMutation.isLoading}
            startIcon={
              reviewMutation.isLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            onClick={handleConfirmReject}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "6px"
            }}
          >
            {reviewMutation.isLoading ? "Rejecting..." : "Confirm Rejection"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Lightbox */}
      {previewImage && (
        <Dialog
          open={Boolean(previewImage)}
          onClose={() => setPreviewImage(null)}
          maxWidth="lg"
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              overflow: "hidden"
            }
          }}
        >
          <Box sx={{ position: "relative", p: 1 }}>
            <IconButton
              onClick={() => setPreviewImage(null)}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "#ffffff",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.85)" },
                zIndex: 2
              }}
            >
              <Close />
            </IconButton>
            <Box
              sx={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "#0f172a",
                borderRadius: "12px",
                p: 1
              }}
            >
              <img
                src={previewImage.url}
                alt={previewImage.title || "Preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  borderRadius: "8px",
                  display: "block",
                  objectFit: "contain"
                }}
              />
              {previewImage.title && (
                <Typography
                  sx={{
                    color: "#cbd5e1",
                    fontSize: "0.78rem",
                    mt: 1,
                    textAlign: "center"
                  }}
                >
                  {previewImage.title}
                </Typography>
              )}
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "0.82rem"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GeoTagPhotosApproval;
